import { Suspense, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import { TopBar } from '@/components/layout/TopBar';
import { Spinner } from '@/components/ui/spinner';
import { config } from '@/config';
import { useRouteFocus } from '@/hooks/use-route-focus';

/**
 * The application shell (05 §1.3): a 56px top bar, then the routed content.
 *
 * The `<main>` landmark carries `tabIndex={-1}` and receives focus on every
 * navigation (`useRouteFocus`), so a keyboard user lands in the new page instead
 * of at the top of a stale document.
 */
export function AppShell() {
  const { t } = useTranslation();
  const mainRef = useRef<HTMLElement | null>(null);
  useRouteFocus(mainRef);

  return (
    <div className="flex min-h-screen flex-col bg-background" data-testid="app-shell">
      <a
        href="#main"
        className="sr-only-focusable absolute z-50 m-2 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground"
      >
        {t('nav.skipToContent')}
      </a>

      <TopBar />

      <main
        id="main"
        ref={mainRef}
        tabIndex={-1}
        className="mx-auto w-full max-w-[1440px] flex-1 px-4 py-6 outline-none sm:px-6"
      >
        <Suspense
          fallback={
            <div className="grid min-h-[40vh] place-items-center" data-testid="route-fallback">
              <Spinner label={t('common.loading')} />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>

      <footer className="border-t px-4 py-3 text-xs text-muted-foreground sm:px-6">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center gap-x-3 gap-y-1">
          <span>
            {t('app.name')} · {t('app.tagline')}
          </span>
          <span className="ms-auto font-mono" dir="ltr">
            {t('app.version')} {config.appVersion}
          </span>
        </div>
      </footer>
    </div>
  );
}
